import { shouldSkipObject } from '@/threejs/objectManager';
import * as THREE from 'three';
import { FullScreenQuad, Pass } from 'three/examples/jsm/postprocessing/Pass.js';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader.js';
import type { Projector } from './projector';
import type { ProjectorFrustumEdge } from './projector-frustum-edge';

export class FrustumEdgePass extends Pass {
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.Camera;
  private readonly frustumEdge: ProjectorFrustumEdge;
  private readonly copyMaterial: THREE.ShaderMaterial;
  private readonly fsQuad: FullScreenQuad;
  private projectors: Projector[];
  private edgeEnabled = false;
  private gizmoScene?: THREE.Scene;
  private projectorTarget = new THREE.Vector3(0, 0, 0);

  constructor(
    scene: THREE.Scene,
    camera: THREE.Camera,
    frustumEdge: ProjectorFrustumEdge,
    projectors: Projector[],
    gizmoScene?: THREE.Scene
  ) {
    super();
    this.scene = scene;
    this.camera = camera;
    this.frustumEdge = frustumEdge;
    this.projectors = projectors;
    this.gizmoScene = gizmoScene;
    this.clear = false;
    this.needsSwap = false;
    this.copyMaterial = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.clone(CopyShader.uniforms),
      vertexShader: CopyShader.vertexShader,
      fragmentShader: CopyShader.fragmentShader,
      depthTest: false,
      depthWrite: false,
    });
    this.copyMaterial.blending = THREE.NoBlending;
    this.fsQuad = new FullScreenQuad(this.copyMaterial);
  }

  setProjectors(projectors: Projector[]) {
    this.projectors = projectors;
  }

  setEdgeEnabled(enabled: boolean) {
    this.edgeEnabled = enabled;
    this.enabled = enabled;
  }

  getEnabled() {
    return this.enabled;
  }

  setProjectorTarget(target: THREE.Vector3) {
    this.projectorTarget.copy(target);
  }

  updateHelperLines() {
    this.frustumEdge.updateHelperLines(this.projectors, this.scene, this.projectorTarget);
  }

  renderGizmoScene(renderer: THREE.WebGLRenderer) {
    if (!this.gizmoScene) {
      return;
    }
    const previousAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.clearDepth();
    renderer.render(this.gizmoScene, this.camera);
    renderer.autoClear = previousAutoClear;
  }

  render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget, readBuffer: THREE.WebGLRenderTarget) {
    const previousAutoClear = renderer.autoClear;
    const previousOverrideMaterial = this.scene.overrideMaterial;
    const previousBackground = this.scene.background;
    renderer.autoClear = false; // Set this FIRST before setRenderTarget

    try {
      renderer.setRenderTarget(readBuffer);

      const visibleProjectors = this.projectors.filter((p) => p.visible);
      if (this.edgeEnabled && visibleProjectors.length > 0) {
        this.scene.overrideMaterial = this.frustumEdge.getOverrideMaterial();
        const batchSize = this.frustumEdge.getBatchSize();
        this.frustumEdge.beginEdgePass();

        // Avoid background force-clear while layering overlays.
        this.scene.background = null;

        const hiddenObjects: THREE.Object3D[] = [];
        this.scene.traverse((object: THREE.Object3D) => {
          if (object.visible && shouldSkipObject(object)) {
            object.visible = false;
            hiddenObjects.push(object);
          }
        });

        for (let i = 0; i < visibleProjectors.length; i += batchSize) {
          const batch = visibleProjectors.slice(i, i + batchSize);
          this.frustumEdge.update(renderer, this.scene, batch, {
            edgeOnly: true,
          });
          renderer.render(this.scene, this.camera);
        }

        hiddenObjects.forEach((object) => (object.visible = true));

        this.frustumEdge.endEdgePass();
      }
    } finally {
      this.scene.background = previousBackground;
      this.scene.overrideMaterial = previousOverrideMaterial;
      renderer.autoClear = previousAutoClear;
    }

    if (this.renderToScreen) {
      this.copyMaterial.uniforms['tDiffuse'].value = readBuffer.texture;
      renderer.setRenderTarget(null);
      this.fsQuad.render(renderer);
    }
  }
}
